/**
 * Created by Carsten Bühler
 * User: CBuehler on 07.04.2016
 * Copyright: xdot GmbH 2015
 */

/**
 * @deprecated use HCTimeSeriesPlugin.js instead
 * User: Valeria Nazarova on 14.02.2023
 * Copyright: xdot GmbH 2015
 */

'use strict';

import { DataUtil } from 'plugin/liferay-widgets-react-web';
import uniqueId, { METRIC_TYPE } from '../../utils/timeseries-util';

const equalArray = (a, b, equal) => {
    if (a && b) {
        if (a.length === b.length) {
            return a.findIndex((ae, i) => {
                return !equal(ae, b[i]);
            }) >= 0;
        }
        return false;
    }
    return !a && !b;
};

export default class TimeSeriesPlugin {

    initOptions(o){
        return this.options(o);
    }

    options(o){
        o = {
            ...o,
            data: {
                ...o.data,
                columns: [],
                empty: {
                    label: {
                        text: 'Daten nicht geladen'
                    }
                }
            },
            tooltip: {
                grouped: false,
                format: {
                    value: function (value, ratio, id){
                        return value.toFixed(2);
                    }
                }
            },
            axis: {
                x: {
                    type: 'timeseries',
                    padding: {
                        right: 1000 * 60 * 60 * 8
                    },
                    tick: {
                        rotate: 45,
                        format: '%d-%m %H:%M', //d3.time.format('%d-%m %H:%M')
                        fit: true,
                        count: () => {
                            return this.chart && this.chart.data().length > 0 ? 25 : 0;
                        }
                    }
                },
                y: {
                    min: 0,
                    max: 100
                }
            }
        };
        return o;
    }

    init(chart, o){
        this.chart = chart;
        chart.zoom.enable(true);
    }

    update(chart, o){
        if (o && o.item && this.lastSeries != o.item) {
            this.updateSeries(chart, DataUtil.asArray(o.item));
        }
        this.lastSeries = o.item;
    }

    updateSeries(chart, seriesArray){
        seriesArray = seriesArray.filter(series => series.times.length > 0);

        if (seriesArray.length == 0) {
            return;
        }

        const referenceValueSeriesArray = seriesArray.filter(series => series.type == METRIC_TYPE.REFERENCE_VALUES);
        if (referenceValueSeriesArray.length > 0) {
            seriesArray = seriesArray.filter(series => series.type != METRIC_TYPE.REFERENCE_VALUES);
        }

        let updateType = 0, anyDataUpdate = false;
        const updates = seriesArray.map(series => {
            const seriesId = uniqueId(series);
            updateType = updateType == -1 ? -1 : this.checkUpdateType(chart, seriesId, series);
            anyDataUpdate = anyDataUpdate || updateType != 0;
            return {
                seriesId,
                updateType,
                series
            };
        });

        //Check for removed Data
        //If removed Data found, do full repaint
        chart.data().forEach(data => {
            let found = false;
            updates.forEach(update => {
                if (data.id == update.seriesId) {
                    found = true;
                }
            });
            if (!found) {
                updateType = -1;
            }
        });

        if (anyDataUpdate) {
            this.updateRegions(chart, seriesArray);
            //See: https://inovum-dev.de/jira/browse/FLIWAS-788
            /*let yAxisRange = */
            this.updateAxis(seriesArray);
        }

        this.updateGrid(referenceValueSeriesArray/*, yAxisRange*/);

        if (updateType == -1) {
            this.fullUpdate(chart, updates);
        } else {
            this.appendOnlyUpdate(chart, updates);
        }

        //ToDo:
        //Currently not supported for splines
        //http://c3js.org/reference.html#data-regions
        //chartData.regions[ 'data_' + name ] = this.getRegionFromSet(series);

    }

    appendOnlyUpdate(chart, updates){
        let chartData = {
            columns: [],
            length: 0
        };

        updates.forEach(update => {
            const { series, seriesId, updateType } = update, axisName = 'x_' + seriesId;
            if (updateType > 0) {

                chartData.columns.push([seriesId].concat(series.values.slice(series.values.length - updateType)));
                chartData.columns.push([axisName].concat(series.times.slice(series.times.length - updateType)));

            }
        });

        if (chartData.columns.length > 0) {
            chart.flow(chartData);
        }
    }

    fullUpdate(chart, updates){
        //http://c3js.org/reference.html#api-load
        let chartData = {
            xs: this.chart.xs(),
            columns: [],
            types: {},
            names: {},
            colors: {},
            regions: {},
            unload: true
        };

        updates.forEach(update => {
            const { series, seriesId } = update, axisName = 'x_' + seriesId;
            chartData.xs[seriesId] = axisName;
            chartData.columns.push([seriesId].concat(series.values));
            chartData.columns.push([axisName].concat(series.times));
            chartData.names[seriesId] = this.getName(series);
            chartData.colors[seriesId] = this.getColor(series);
            chartData.types[seriesId] = this.getTypeOfChart(series);
        });

        if (updates.length > 0) {
            chart.load(chartData);
        } else {
            this.chart.unload();
        }
    }

    updateAxis(seriesArray){
        const { min, max } = this.getAxisMinMax(seriesArray);
        if (min != Number.MAX_VALUE && max > 0 && (this.chart.axis.min().y != min || this.chart.axis.max().y != max)) {
            this.chart.axis.min({ y: min });
            this.chart.axis.max({ y: max });
        }
        //See: https://inovum-dev.de/jira/browse/FLIWAS-788
        //return { min: min, max: max };
    }

    //See: https://inovum-dev.de/jira/browse/FLIWAS-788
    updateGrid(referenceValueSeriesArray/*, yAxisRange*/){
        //let i = 0;
        const lines = referenceValueSeriesArray.map(rvS => {
            if (rvS.values && rvS.values.length > 0) {
                const rv = rvS.values[rvS.values.length - 1];
                return {
                    value: rv,
                    text: rvS.meta.title,
                    class: 'color-' + rvS.meta.color
                };
            } else {
                console.warn('Empty ReferenceValueSeries', rvS);
            }
        }).filter(rvS => !!rvS);

        //See: https://inovum-dev.de/jira/browse/FLIWAS-788
        /*for (i = yAxisRange.min; i <= yAxisRange.max; i += 20) {
            lines.push({
                value: i,
                class: 'dashed-line'
            });
        }*/

        const current = this.chart.ygrids();
        if (!equalArray(current, lines, (a, b) => (a.value === b.value && a.text === b.text && a.class === b.class))) {
            this.chart.ygrids(lines);
        }

    }

    updateRegions(chart, seriesArray){
        const regions = this.getChartRegions(seriesArray);
        if (regions && Array.isArray(regions)) {
            const current = this.chart.regions();
            if (!equalArray(current, regions, (a, b) => (a.start === b.start && a.end === b.end))) {
                chart.regions(regions);
            }
        }
    }

    /**
     *  Check type of the series update, returns:
     *      -1  a 'full' update needed
     *      0  no update data is equal
     *      n number of values that needs to be appended
     */
    checkUpdateType(chart, seriesId, newSeries){
        let data = chart.data(seriesId), newLen = newSeries.values.length;
        data = data.length == 0 ? null : data[0];

        if (!data || newLen < data.values.length) {
            return -1;
        }

        if (this.endsWith(data.values, newSeries)) {

            if (data.values.length == newLen) {
                return 0;
            }

            if (data.values[data.values.length - 1].x.getTime() > newSeries.times[newSeries.times.length - 1]) {
                console.error('Invalid update found! ', data, newSeries);
                return -1;
            }

            return newLen - data.values.length;
        }

        return -1;
    }

    endsWith(cvalues, { values, times }){
        if (cvalues.length > values.length) {
            return false;
        }

        for (let i = cvalues.length - 1, v ; i >= 0 ; i--) {
            v = cvalues[i];
            if (v.value !== values[i] ||
                v.x.getTime() !== times[i]) {
                return false;
            }
        }
        return true;
    }

    getTypeOfChart(series){
        switch (series.type) {
            case METRIC_TYPE.DATA_POINTS:
                return 'scatter';
            case METRIC_TYPE.SERIES:
                return 'line';
            default:
                console.warn('Unknown series type: ' + series.type);
                return 'line';
        }
    }

    getChartRegions(seriesArray){
        let min = Number.MAX_VALUE, max = 0, regions = [];

        seriesArray.forEach(series => {
            min = Math.min(min, series.times[0]);
            max = Math.max(max, series.times[series.times.length - 1]);
        });

        if (min > 0 && max > 0 && min < max) {
            if (max - (1000 * 60 * 60 * 24 /* 1 DAY */) > min) {
                let i = 0;
                let minDate = new Date(min);
                let maxDate = new Date(max);

                let midnight = new Date(minDate.setHours(0, 0, 0, 0));
                let midnightNextDay = new Date(new Date(midnight.getTime()).setHours(24, 0, 0, 0));

                regions.push({
                    start: minDate.getTime(),
                    end: midnightNextDay.getTime()
                });
                i++;

                midnight = new Date(midnightNextDay.getTime());
                midnightNextDay = new Date(new Date(midnight.getTime()).setHours(24, 0, 0, 0));

                while (midnightNextDay.getTime() < maxDate.getTime()) {
                    if (i % 2 == 0) {
                        regions.push({
                            start: midnight.getTime(),
                            end: midnightNextDay.getTime()
                        });
                    }

                    midnight = new Date(midnightNextDay.getTime());
                    midnightNextDay = new Date(new Date(midnight.getTime()).setHours(24, 0, 0, 0));
                    i++;
                }

                if (i % 2 == 0) {
                    regions.push({
                        start: midnight.getTime(),
                        end: maxDate.getTime()
                    });
                }
            }

        }
        return regions;
    }

    getName(series){
        return series.metric;
    }

    getColor(series){

    }

    getMinMaxValues(series){
        let min = Number.MAX_VALUE, max = 0;
        if (Array.isArray(series)) {
            series.forEach(s => {
                max = Math.max(max, Math.max.apply(Math, s.values));
                min = Math.min(min, Math.min.apply(Math, s.values));
            });
        } else {
            max = Math.max.apply(Math, series.values);
            min = Math.min.apply(Math, series.values);
        }
        return { min, max };
    }

    getAxisMinMax(seriesArray){
        let { min, max } = this.getMinMaxValues(seriesArray);
        min = Math.floor(min / 100) * 100;
        if (max > 0) {
            max = Math.ceil(max / 100) * 100;
        }
        return { min, max };
    }

    //TODO CURRENTLY UNUSED
    getRegionFromSet(seriesArray){

    }

}




// WEBPACK FOOTER //
// ./js/charts/plugins/TimeSeriesPlugin.js